import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { CompoundEntityRef, parseEntityRef } from '@backstage/catalog-model';
import { ReadEventClient } from '@internal/plugin-neo4j-techdocs-common';
import useAsync from 'react-use/esm/useAsync';

/** A doc read with its timestamp in epoch milliseconds. */
export type RecentRead = {
  entityRef: CompoundEntityRef;
  timestamp: number | undefined;
};

type useLastReadEventType = {
  value: RecentRead[];
  loading: boolean;
  error: Error | undefined;
};

export const useUserRecentReadComponents = (
  limit = 10,
  dependencies: any[] = [],
): useLastReadEventType => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const readClient = new ReadEventClient({ discoveryApi, fetchApi });
  const {
    loading,
    error,
    value: entityRefs,
  } = useAsync(async () => {
    const identity = await identityApi.getBackstageIdentity();
    const username = parseEntityRef(identity.userEntityRef).name;
    const getReadEventsResponse = await readClient.userGet({
      path: { user: username },
      query: {
        limit: limit,
        order: 'desc',
      },
    });
    if (getReadEventsResponse.ok) {
      const result = (await getReadEventsResponse.json())
        .filter(
          (event): event is { docEntityRef: string; timestamp?: number } =>
            typeof event.docEntityRef === 'string',
        )
        .map(event => ({
          entityRef: parseEntityRef(event.docEntityRef),
          timestamp: event.timestamp,
        }));
      return result;
    }
    throw new Error(
      `Failed to get query read events: ${getReadEventsResponse.statusText}`,
    );
  }, dependencies);
  return {
    value: entityRefs ?? [],
    loading,
    error,
  };
};
